import { Parse } from 'parse';
Parse.initialize(process.env.REACT_APP_APP_ID, null, process.env.REACT_APP_MASTER_KEY);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const handleParseError = async (error) => {
  if (error.code === 209 || error.message?.includes('Invalid session token')) {
    try {
      await Parse.User.logOut();
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
    }

    localStorage.clear();
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }
  throw error;
};

/**
 * Activity logging is deliberately NOT done here.
 *
 * Writing the log from the browser meant it only fired for one role and could
 * be skipped by editing localStorage. Every write below now goes through a
 * cloud function that records the change server-side, where it cannot be
 * bypassed. See backend src/services/activityLog.service.js.
 */

export const dataProvider = {
  create: async (resource, params) => {
    try {
      if (resource === 'GameCatalogue') {
        // Parse JS SDK can't reliably create with a custom objectId from the
        // client, so creation is delegated to the cloud function, which also
        // enforces the role check and writes the activity log.
        const {
          objectId,
          gameId,
          name,
          category,
          description,
          type,
          gameSequence,
          building,
          slug,
          gameURL
        } = params.data;

        const result = await Parse.Cloud.run('createGameCatalogue', {
          objectId: objectId ? String(objectId).trim() : undefined,
          gameId: gameId ? Number(gameId) : undefined,
          name,
          category,
          description,
          type,
          gameSequence:
            gameSequence !== undefined && gameSequence !== '' ? Number(gameSequence) : undefined,
          building,
          slug: slug ? String(slug).trim() : undefined,
          gameURL: gameURL ? String(gameURL).trim() : undefined
        });

        return { data: { id: result.id || result.objectId, ...result } };
      }

      const Resource = Parse.Object.extend(resource);
      const obj = new Resource();
      const result = await obj.save(params.data);

      return { data: { id: result.id, ...result.attributes } };
    } catch (error) {
      return handleParseError(error);
    }
  },
  getOne: async () => {},
  getList: async (resource, params) => {
    Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const filter = params.filter;

    let query;
    let count;

    if (resource === 'GameBetConfig') {
      const Resource = Parse.Object.extend('GameBetConfig');
      query = new Parse.Query(Resource);
      query.include('gameCatalogueId');
      if (filter?.gameName) {
        query.matches('gameName', filter.gameName, 'i');
      }
    } else if (resource === 'GameCatalogue') {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      query.include('building');
      if (filter) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === 'string') {
            query.matches(f, filter[f], 'i');
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      }
    } else {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      if (filter) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === 'string') {
            query.matches(f, filter[f], 'i');
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      }
    }

    count = await query.count({ useMasterKey: true });

    query.skip((page - 1) * perPage);
    query.limit(perPage);

    if (field && order) {
      const sortField = field === 'id' ? 'objectId' : field;
      if (order === 'DESC') {
        query.descending(sortField);
      } else if (order === 'ASC') {
        query.ascending(sortField);
      }
    }

    try {
      const results = await query.find({ useMasterKey: true });
      const visibleResults =
        resource === 'GameCatalogue' ? results : results.filter((o) => o.get('isActive') !== false);

      const data = visibleResults.map((o) => {
        if (resource === 'GameBetConfig') {
          const betLevels = o.get('betLevels') || [];
          return {
            id: o.id,
            gameSlug: o.get('gameSlug'),
            gameName: o.get('gameName'),
            betLevels,
            minBet: o.get('minBet'),
            maxBet: o.get('maxBet'),
            defaultBetLevel: o.get('defaultBetLevel'),
            stepBet: o.get('stepBet'),
            updatedAt: o.get('updatedAt')
          };
        } else if (resource === 'GameCatalogue') {
          return {
            id: o.id,
            name: o.get('name'),
            category: o.get('category'),
            description: o.get('description'),
            type: o.get('type'),
            objectId: o.get('objectId'),
            gameId: o.get('gameId'),
            gameSequence: o.get('gameSequence'),
            isActive: o.get('isActive'),
            live_version: o.get('live_version'),
            gameURL: o.get('gameURL'),
            slug: o.get('slug'),
            createdAt: o.get('createdAt'),
            updatedAt: o.get('updatedAt'),
            building: o.get('building')
              ? {
                  id: o.get('building').id,
                  name: o.get('building').get('name')
                }
              : null
          };
        }
        return { id: o.id, ...o.attributes };
      });

      return {
        data,
        total: count
      };
    } catch (error) {
      return handleParseError(error);
    }
  },
  update: async (resource, params) => {
    const excludeFields = [
      'id',
      'sessionToken',
      'createdAt',
      'updatedAt',
      'ACL',
      'confirmPassword'
    ];

    const data = Object.keys(params.data)
      .filter((key) => !excludeFields.includes(key))
      .reduce((result, key) => {
        result[key] = params.data[key];
        return result;
      }, {});

    try {
      if (resource === 'GameCatalogue') {
        const result = await Parse.Cloud.run('updateGameCatalogue', {
          objectId: params.id,
          ...data
        });
        return { data: { id: result.id || result.objectId, ...result } };
      }

      if (resource === 'gameCatalogueSwitch') {
        // The list's isActive / comingSoon switches.
        const field = Object.keys(params.data)[0] || 'isActive';
        const result = await Parse.Cloud.run('toggleGameCatalogueField', {
          objectId: params.id,
          field,
          value: params.data[field]
        });
        return { data: { id: result.id || result.objectId, ...result } };
      }

      const Resource = Parse.Object.extend(resource);
      const query = new Parse.Query(Resource);
      const obj = await query.get(params.id, { useMasterKey: true });
      const saved = await obj.save(data, { useMasterKey: true });

      return { data: { id: saved.id, ...saved.attributes } };
    } catch (error) {
      console.error('Error updating resource:', error);
      throw new Error(error.message);
    }
  },
  getMany: async (resource, params) => {
    let results = null;

    if (resource === 'GameCatalogue') {
      results = params.ids
        .map((obj) => {
          if (typeof obj === 'string')
            return new Parse.Query('VegasBuilding').get(obj, {
              useMasterKey: true
            });
        })
        .filter((n) => n);
    } else {
      const Resource = Parse.Object.extend(resource);
      results = params.ids.map((id) => new Parse.Query(Resource).get(id));
    }
    const data = await Promise.all(results);
    return {
      data: data.map((o) => ({ id: o.id, ...o.attributes }))
    };
  },
  delete: async (resource, params) => {
    try {
      if (resource === 'GameCatalogue') {
        // The cloud function keeps the existing rule: an Admin soft-deletes,
        // anyone above removes the row outright.
        await Parse.Cloud.run('deleteGameCatalogue', { objectId: params.id });
        return { data: { id: params.id } };
      }

      const Resource = Parse.Object.extend(resource);
      const query = new Parse.Query(Resource);
      const result = await query.get(params.id);
      const data = { data: { id: result.id, ...result.attributes } };

      const isAdmin = String(localStorage.getItem('role') || '').toLowerCase() === 'admin';
      if (isAdmin && Object.prototype.hasOwnProperty.call(result.attributes, 'isActive')) {
        result.set('isActive', false);
        await result.save(null, { useMasterKey: true });
      } else {
        await result.destroy({ useMasterKey: true });
      }

      return data;
    } catch (error) {
      return handleParseError(error);
    }
  }
};
